import { getT } from "../../i18n";
import type { ShareableConfig } from "../../lib/shareConfig";
import type { useBuilderStore } from "../../store/useBuilderStore";
import type { SaunaType } from "../../types/design";

type BuilderState = ReturnType<typeof useBuilderStore.getState>;

/**
 * The whole design as one lossless snapshot. A share link and the device-local
 * draft are the same thing — "everything needed to reopen this design" — so
 * they are built by one function; a field added here travels with both.
 */
export function buildShareableConfig(
	state: BuilderState,
	type: SaunaType,
): ShareableConfig {
	const { thermalInputs } = state;
	return {
		market: state.market,
		locationMode: state.locationMode,
		climateProfile:
			state.locationMode === "manual_climate"
				? state.climateProfile
				: undefined,
		type,
		width: state.dimensions.width,
		length: state.dimensions.length,
		...(type === "tvarum" ? state.twoRoomConfig : {}),
		roofConfig: state.roofConfig,
		heaterType: thermalInputs.heaterType,
		heaterModelId: thermalInputs.heaterModelId,
		glassAreaSqm: thermalInputs.glassAreaSqm,
		stoneAreaSqm: thermalInputs.stoneAreaSqm,
		isOutdoorLogCabin: thermalInputs.isOutdoorLogCabin,
		includesShower: thermalInputs.includesShower,
		saunaLighting: thermalInputs.saunaLighting,
		heaterPlacementZMm: thermalInputs.heaterPlacementZMm,
		siteConditions: state.siteConditions,
		// A design whose benches the user moved must reopen as THAT design; a
		// snapshot without the layout quotes the generated default's bench buy.
		interiorLayout: state.interiorLayout,
		// Karaktärsval travel for the same reason: a design restyled in black
		// slamfärg must reopen black, and its quoted total is the restyled one.
		finishes: state.finishes,
		// The height moves the 3D, the kW and the price, so it travels too.
		wallHeightM: state.wallHeightM,
		// Existing walls and the house above move the BOM and the chimney.
		indoorRoom: state.indoorRoom,
		openings: state.openings,
		partitionSplit: state.partitionSplit,
		// The orientation is a placement decision, not a viewing condition —
		// dropping it would show the building on the wrong side of the plot.
		rotationDeg: state.rotationDeg,
		capacityHint: state.wizardCapacityHint,
		deckConfig: state.deckConfig,
		postalCode: state.postalCode || undefined,
		projectName:
			state.projectName !== getT("se").rail_defaultProjectName &&
			state.projectName !== getT("intl").rail_defaultProjectName
				? state.projectName
				: undefined,
	};
}
